import {
  STANCES,
  applyCoreAttackStyle,
  createCoreDieFaces,
  getCoreDieFaceValues,
  isValidCoreDieRoll,
} from './coreAttackStyles';
import {
  CORE_ATTACKS,
  getCoreDamageFaces,
  isCoreDamageRoll,
} from './coreDamage';
import { CORE_SECTOR } from './runConstants';
import { recordBossBattleRound } from './bossBattleStats';
import { acquireShards, getTotalShardsAcquired } from './shardTotals';
import { applyCoreBoardPhase, strengthenCoreBoard } from './coreBoardPhases';
import {
  consumeRelicHealingEffects,
  getRelicBlockEffects,
  getRelicHealingEffects,
  getRelicShardEffects,
} from './relicEffects';
import {
  RELICS,
  applyHealing,
  finishBoardTileEffects,
  completeBoardRelaunch,
  getBoardTreasureRewards,
  getMaxHp,
  flipRoll,
  getBaseRoll,
  getBoardAdjustedRoll,
  getRollRelicEffects,
  getRecentRooms,
  getVictoryScore,
  getDefeatScore,
} from './game';
import {
  DOOR_BOARD_VERSION,
  migratePlayerBoardBalance,
  BOSS_BOARD_VERSION,
  generateDoorBoard,
  generateBossDoorBoard,
  getBoardSquare,
  applyBoardSquareRoll,
  replaceRetiredBossTile,
  withPlayerBoardCorners,
} from './doorBoard';
import { createEmptyPortalPair } from './portalPair';
import { isBossRelic } from './relics';
import { isBossShopUpgrade, getRelicPrice } from './shop';
import { isShopRelic } from './relicDrops';
import { consumeDecoyBeacon, getDecoyBeaconState } from './decoyBeacon';
import {
  canTriggerLastStand,
  didLastStandSurvive,
  getLastStandEncounter,
  requestLastStandRoll,
} from './lastStandProtocol';
import {
  commitChainReaction,
  getBoardEffectTotal,
  getChainReactionActivation,
} from './chainReaction';
import { startThrowCombo } from './throwCombo';
import { formatDieValue, getAttackDieFaceValues, isValidDieRoll } from './dice';
import { getBoardRelaunchRequest } from './powerUp';
import { needsBoardTileChoice } from './boardTileEffects';
import { createBossUpgradeRolls } from './bossUpgradeRolls';
import {
  BOSS_MAX_HP,
  CORE_PHASES,
  CORE_PHASE_RANK,
  getCorePhaseForHp,
} from './corePhases';

export { STANCES } from './coreAttackStyles';
export { BOSS_MAX_HP, getCorePhaseForHp } from './corePhases';

const getAdvancedCorePhase = (phase, previousHp, nextHp) => {
  const candidates = [
    CORE_PHASE_RANK[phase] === undefined ? CORE_PHASES.CONTAINMENT : phase,
    getCorePhaseForHp(previousHp),
    getCorePhaseForHp(nextHp),
  ];
  return candidates.reduce((highest, candidate) =>
    CORE_PHASE_RANK[candidate] > CORE_PHASE_RANK[highest] ? candidate : highest
  );
};

export const getCorePhase = (game) =>
  getAdvancedCorePhase(game.corePhase, BOSS_MAX_HP, game.bossHp ?? BOSS_MAX_HP);

const getBossRoundState = (game) =>
  game.bossRoundState?.round === game.bossRound ? game.bossRoundState : null;

const getBossStartingHealth = (game) => {
  const round = getBossRoundState(game);
  return round
    ? {
        hp: round.hpBefore,
        maxHp: round.maxHpBefore,
        bossHp: round.bossHpBefore,
      }
    : { hp: game.hp, maxHp: getMaxHp(game), bossHp: game.bossHp };
};

const ORIGINAL_BOSS_RELIC_NOTES = {
  chainReaction:
    'Automatically activates on your next throw: the landing, then up, right, down, and left once each. The view widens to show all activated tiles. Numeric effects resolve in that order; healing and shields stack, and every chest grants a relic. Both good and bad tiles apply. One activation for the entire run, shared with earlier sectors. Recharge cannot restore it.',
  lastStandProtocol:
    'A lethal counterattack pauses for one survival throw. Beat 75 after board and roll-relic bonuses to survive at 1 HP. Consumed after the attempt, on success or failure. Siphon healing requires actual player HP loss.',
  decoyBeacon:
    'Set up a decoy before your throw or while the Core waits for you to start its turn. It takes one hit for you. Misses and fully blocked attacks leave it ready for later. One use per Core fight.',
  ghostBump:
    'While the Core’s die is moving, tap toward a tile or press Space to nudge it. This can change its number or landing tile. One use for the whole Core fight.',
  phaseAnchor:
    'Ready automatically. Activate during your board throw to slow your die for 4 seconds while you choose a bump. One use for the whole Core fight, spent only on activation.',
  mirrorChip:
    'Automatically flip the digits on your die if it helps: 29 becomes 92. Works before tile effects, once during this Core fight.',
  firewall: 'Block 1 HP on every boss hit.',
  overclock: '+12 damage each round; boss hits deal +1 HP.',
  luckySeven: 'If your roll contains a 7 after tile effects, add 20 damage.',
  glitchBattery:
    'If your roll is 10 or lower after tile effects, add 30 damage.',
  echoCore:
    'If your roll has matching digits after tile effects, such as 22, add 18 damage.',
  prismDrive: 'If your roll is even after tile effects, add 8 damage.',
};

export const BOSS_RELIC_NOTES = Object.fromEntries(
  Object.entries(RELICS).map(([id, relic]) => [
    id,
    ORIGINAL_BOSS_RELIC_NOTES[id] ||
      (relic.bossOnly
        ? relic.description
        : relic.rollBonus && isBossRelic(id)
        ? relic.description
        : isBossRelic(id)
        ? `${relic.description} Its effect also applies during the Core fight.`
        : relic.successHeal || relic.successShards
        ? `${relic.description} Victory rewards apply when the Core is defeated.`
        : 'No direct effect against the Core.'),
  ])
);

export const getBossIntent = (game) => {
  const intent = CORE_ATTACKS[(game.bossRound - 1) % CORE_ATTACKS.length];
  // A surviving player hit can activate Meltdown before this counterattack.
  const phase = getCorePhase(game);
  const enraged = phase === CORE_PHASES.MELTDOWN;
  const interference = game.boardTileResolution?.interference || 0;
  return {
    ...intent,
    faces: getCoreDamageFaces(game.bossRound, enraged),
    interference,
    damage: Math.max(...getCoreDamageFaces(game.bossRound, enraged)),
    phase,
    enraged,
  };
};

export const migrateRetiredBossTiles = (game) => {
  if (
    !Array.isArray(game?.bossDoorBoard) ||
    !game.bossDoorBoard.some((square) => square?.effect?.type === 'skull')
  )
    return game;
  const bossDoorBoard = game.bossDoorBoard.map(replaceRetiredBossTile);
  const round = game.bossRoundState;
  // Pending calculations must use the replacement. Already committed impacts
  // retain their saved health and cannot deal damage again during migration.
  const committed = round?.counterImpactApplied || round?.coreSelfImpactApplied;
  return {
    ...game,
    bossDoorBoard,
    bossBoardModifier: bossDoorBoard[game.bossLandingIndex]?.modifier || 0,
    bossRoundState:
      round && !committed
        ? {
            ...round,
            lockedCounter: null,
            lockedHealth: null,
            counterCalculationRows: [],
          }
        : round,
  };
};

// Keep the existing tile positions, sabotage, health commits, and attack
// snapshot when upgrading a saved fight. Only the old probability scale changes.
export const migrateBossDamage = (game) => {
  game = migrateRetiredBossTiles(migratePlayerBoardBalance(game));
  if (
    !game ||
    game.bossCombatVersion === 2 ||
    !['bossReady', 'bossPlayerRolled', 'bossRolled', 'bossResult'].includes(
      game.phase
    )
  )
    return game;
  const faces = getBossIntent(game).faces;
  const convertSquare = (square) => ({
    ...square,
    coreDamage: true,
    modifier: square.coreDamage
      ? square.modifier
      : square.modifier
      ? Math.sign(square.modifier) * (Math.abs(square.modifier) <= 20 ? 1 : 2)
      : 0,
  });
  const next = {
    ...game,
    bossCombatVersion: 2,
    bossDoorBoardVersion: BOSS_BOARD_VERSION,
    bossDoorBoard:
      game.bossDoorBoard?.length === 25
        ? game.bossDoorBoard.map(convertSquare)
        : generateBossDoorBoard(),
    bossRoll: Number.isInteger(game.bossRoll)
      ? faces[
          Math.min(5, Math.max(0, Math.ceil((game.bossRoll * 6) / 100) - 1))
        ]
      : null,
    bossMassiveAttackDamage: null,
    boardTileResolution: game.boardTileResolution
      ? {
          ...game.boardTileResolution,
          interference: Math.ceil(
            (game.boardTileResolution.interference || 0) / 15
          ),
          pending:
            game.boardTileResolution.pending?.type === 'sabotage'
              ? {
                  ...game.boardTileResolution.pending,
                  changes: game.boardTileResolution.pending.changes?.map(
                    (change) => ({
                      ...change,
                      before: convertSquare(change.before),
                      after: convertSquare(change.after),
                    })
                  ),
                }
              : game.boardTileResolution.pending,
        }
      : game.boardTileResolution,
  };
  next.bossBoardModifier =
    next.bossDoorBoard[next.bossLandingIndex]?.modifier || 0;
  // An attack that already landed must retain its exact recorded outcome.
  if (game.bossRoundState?.counterImpactApplied) {
    next.bossRoundState = {
      ...game.bossRoundState,
      lockedCounter: getBossCounterAttack(next),
      lockedHealth: getBossRoundHealth(next),
    };
    next.bossRoundState.lockedHealth.hpAfterImpact = game.hp;
    next.bossRoundState.lockedCounter.damage = Math.max(
      0,
      next.bossRoundState.lockedHealth.hpAfterHealing - game.hp
    );
    const health = next.bossRoundState.lockedHealth;
    health.bossHealed =
      next.bossRoundState.lockedCounter.damage > 0 &&
      !next.bossRoundState.lockedCounter.won
        ? Math.min(
            getBossIntent(next).heal,
            BOSS_MAX_HP - health.bossHpAfterSkull
          )
        : 0;
    health.bossHpAfterHealing = game.bossRoundState.coreHealingApplied
      ? game.bossHp
      : health.bossHpAfterSkull + health.bossHealed;
  }
  return next;
};

export const buyRelic = (game, id) => {
  if (
    game.phase !== 'shop' ||
    !isShopRelic(id) ||
    (game.floor === CORE_SECTOR
      ? !isBossShopUpgrade(id, game)
      : !!RELICS[id].bossOnly) ||
    !game.shopOffers?.includes(id) ||
    game.relics.includes(id) ||
    game.marketPurchased === game.floor ||
    game.shards < getRelicPrice(game, id)
  )
    return game;
  return {
    ...game,
    shards: game.shards - getRelicPrice(game, id),
    totalShardsAcquired: getTotalShardsAcquired(game),
    relics: [...game.relics, id],
    marketPurchased: game.floor,
    marketPurchase: { floor: game.floor, id },
  };
};

export const beginBoss = (game) =>
  game.phase !== 'shop'
    ? game
    : {
        ...game,
        phase: 'bossReady',
        bossCombatVersion: 2,
        boardTileResolution: null,
        powerUp: null,
        throwCombo: null,
        phaseAnchor: { ...game.phaseAnchor, armed: false },
        portalPair: createEmptyPortalPair(),
        ghostBump: { ...game.ghostBump, armed: false, throwRound: null },
        decoyBeacon: { ...game.decoyBeacon, throwRound: null },
        roomId: 'boss',
        boardCondition: null,
        bossHp: BOSS_MAX_HP,
        corePhase: CORE_PHASES.CONTAINMENT,
        coreBoardPhase: CORE_PHASES.CONTAINMENT,
        coreCorruptedRows: [],
        corePhaseSpell: null,
        bossRound: 1,
        lastCoreAttack: null,
        bossBattleStats: null,
        bossRoundState: null,
        coreShield: 0,
        coreStyleVersion: 1,
        coreDieFaces: createCoreDieFaces(game.selectedDie),
        coreDieCurrentFaces: null,
        roll: null,
        bossRoll: null,
        bossMassiveAttackDamage: null,
        doorBoard: generateDoorBoard(Math.random, { core: true }),
        doorBoardVersion: DOOR_BOARD_VERSION,
        bossDoorBoard: generateBossDoorBoard(),
        bossDoorBoardVersion: BOSS_BOARD_VERSION,
        landingIndex: null,
        bossLandingIndex: null,
        boardModifier: 0,
        bossBoardModifier: 0,
        floorFlipUsed: false,
        lastResult: null,
        bossUpgradeRolls: {},
        boardTreasureReward: null,
        boardTreasureRewards: [],
        chainReactionActivation: null,
      };

export const chooseStance = (game, stance) =>
  game.phase === 'bossReady' && !game.corePhaseSpell && STANCES[stance]
    ? { ...game, stance }
    : game;

export const rollBoss = (game, roll, bossRoll = null, random = Math.random) => {
  if (
    game.phase !== 'bossReady' ||
    game.corePhaseSpell ||
    !isValidDieRoll(roll, game.selectedDie) ||
    (bossRoll !== null &&
      !isCoreDamageRoll(bossRoll, getBossIntent(game).faces))
  )
    return game;
  game = migrateRetiredBossTiles(migratePlayerBoardBalance(game));
  return {
    ...startThrowCombo(game),
    phase: bossRoll === null ? 'bossPlayerRolled' : 'bossRolled',
    boardTileResolution: null,
    powerUp: null,
    bossRoundState: null,
    doorBoard:
      Array.isArray(game.doorBoard) && game.doorBoard.length === 25
        ? game.doorBoard
        : generateDoorBoard(random, { core: true }),
    doorBoardVersion: DOOR_BOARD_VERSION,
    bossDoorBoard:
      Array.isArray(game.bossDoorBoard) && game.bossDoorBoard.length === 25
        ? game.bossDoorBoard
        : generateBossDoorBoard(),
    bossDoorBoardVersion: BOSS_BOARD_VERSION,
    coreStyleVersion: 1,
    coreDieCurrentFaces: null,
    roll: applyCoreAttackStyle({ ...game, coreStyleVersion: 1 }, roll),
    bossRoll,
    bossMassiveAttackDamage: null,
    flipped: false,
    landingIndex: null,
    bossLandingIndex: null,
    boardModifier: 0,
    bossBoardModifier: 0,
    bossUpgradeRolls: createBossUpgradeRolls(game, random),
    boardTreasureReward: null,
    boardTreasureRewards: [],
    chainReactionActivation: null,
  };
};

export const rollBossCounter = (game, bossRoll) =>
  game.phase === 'bossPlayerRolled' &&
  !game.corePhaseSpell &&
  isCoreDamageRoll(bossRoll, getBossIntent(game).faces)
    ? {
        ...game,
        phase: 'bossRolled',
        bossRoll,
        bossLandingIndex: null,
        bossBoardModifier: 0,
      }
    : game;

export const commitBossBoardRoll = (
  game,
  landingIndex,
  actor = 'player',
  settledRoll = null,
  random = Math.random,
  relaunchId = null,
  faceValues = null
) => {
  if (actor === 'player' && getBoardRelaunchRequest(game)) {
    const next = completeBoardRelaunch(
      game,
      landingIndex,
      relaunchId,
      random,
      faceValues,
      settledRoll
    );
    return next !== game && faceValues?.length === 6
      ? { ...next, coreDieCurrentFaces: [...faceValues] }
      : next;
  }
  if (
    !['bossPlayerRolled', 'bossRolled'].includes(game.phase) ||
    (settledRoll !== null &&
      !(actor === 'boss'
        ? isCoreDamageRoll(settledRoll, getBossIntent(game).faces)
        : isValidCoreDieRoll(game, settledRoll))) ||
    !Array.isArray(actor === 'boss' ? game.bossDoorBoard : game.doorBoard) ||
    !Number.isInteger(landingIndex) ||
    landingIndex < 0 ||
    landingIndex >=
      (actor === 'boss' ? game.bossDoorBoard : game.doorBoard).length
  )
    return game;
  // A settled throw is locked, including its one-time chest reward.
  if (
    Number.isInteger(
      actor === 'boss' ? game.bossLandingIndex : game.landingIndex
    )
  )
    return game;
  const landing = getBoardSquare(
    actor === 'boss' ? game.bossDoorBoard : game.doorBoard,
    landingIndex
  );
  if (actor === 'boss')
    return {
      ...game,
      bossRoll: Number.isInteger(settledRoll) ? settledRoll : game.bossRoll,
      bossLandingIndex: landingIndex,
      ghostBump: { ...game.ghostBump, armed: false },
      bossBoardModifier: landing?.modifier ?? 0,
      bossMassiveAttackDamage: null,
      // This die lands after the player's hit. Its shield protects the next hit.
      coreShield:
        landing?.effect?.type === 'shield' ? landing.effect.amount : 0,
    };
  const settled = { ...commitChainReaction(game, landingIndex), landingIndex };
  return finishBoardTileEffects(
    {
      ...settled,
      roll: Number.isInteger(settledRoll) ? settledRoll : game.roll,
      coreDieCurrentFaces:
        faceValues?.length === 6 ? [...faceValues] : game.coreDieCurrentFaces,
      phaseAnchor: { ...game.phaseAnchor, armed: false },
      landingIndex,
      boardModifier: landing?.modifier ?? 0,
    },
    random
  );
};

export const getBossBoardAdjustedRoll = (game) => {
  if (!Number.isInteger(game.bossRoll)) return null;
  const square = getBoardSquare(game.bossDoorBoard, game.bossLandingIndex);
  return applyBoardSquareRoll(game.bossRoll, { ...square, coreDamage: true });
};

export const getBossAttack = (game) => {
  const lockedAttack = getBossRoundState(game)?.attack;
  if (lockedAttack) return lockedAttack;
  const boardRoll = getBoardAdjustedRoll(game) ?? getBaseRoll(game);
  const baseDamage = Math.max(1, boardRoll);
  const effects = getRollRelicEffects(game).map((effect) => ({
    ...effect,
    message: `+${effect.bonus} damage · ${RELICS[effect.id].name}`,
  }));
  const chainActivation = getChainReactionActivation(game);
  if (chainActivation) effects.unshift(chainActivation);
  const chaosDamage = game.bossUpgradeRolls?.chaosCapacitor?.damage || 0;
  if (chaosDamage > 0) {
    effects.push({
      id: 'chaosCapacitor',
      bonus: chaosDamage,
      message: `+${chaosDamage} random damage · Chaos Capacitor`,
    });
  }
  const damageBeforeCritical =
    baseDamage + effects.reduce((sum, effect) => sum + effect.bonus, 0);
  const critical = !!game.bossUpgradeRolls?.criticalMatrix?.critical;
  if (critical) {
    effects.push({
      id: 'criticalMatrix',
      bonus: 0,
      message: 'Critical Matrix doubled this Core attack',
    });
  }
  const damageBeforeShield = damageBeforeCritical * (critical ? 2 : 1);
  const boardShieldAmount = game.coreShield || 0;
  const boardShieldBlocked = Math.min(damageBeforeShield, boardShieldAmount);
  return {
    naturalRoll: getBaseRoll(game),
    boardRoll,
    baseDamage,
    effects,
    critical,
    damageBeforeCritical,
    damageBeforeShield,
    boardShieldBlocked,
    damage: damageBeforeShield - boardShieldBlocked,
  };
};

export const getBossPlayerHealing = (game) => {
  const lockedHealing = getBossRoundState(game)?.healing;
  if (lockedHealing) return lockedHealing;
  const relicEffects = getRelicHealingEffects(game, {
    adjustedRoll: getBoardAdjustedRoll(game),
  });
  const patchHeal =
    relicEffects.find(({ id }) => id === 'patchKit')?.amount || 0;
  const relicHeal = relicEffects
    .filter(({ id }) => id !== 'patchKit')
    .reduce((sum, { amount }) => sum + amount, 0);
  const randomHeal = game.bossUpgradeRolls?.naniteSwarm?.heal || 0;
  const swarmHeal = randomHeal;
  const boardHeal = getBoardEffectTotal(game, 'heal');

  return {
    patchHeal,
    relicHeal,
    relicEffects,
    swarmHeal,
    boardHeal,
    healed: patchHeal + relicHeal + swarmHeal + boardHeal,
  };
};

export const getBossVictoryDie = (game) => {
  const result = game.lastResult;
  if (result?.victoryDie?.faceValues?.length === 6) return result.victoryDie;
  // Older victories can recover the finishing face from the recorded damage.
  const damage =
    result?.bossSelfDamage ||
    (Number.isFinite(result?.attackDamageBeforeShield)
      ? Math.max(
          0,
          result.attackDamageBeforeShield - (result.boardShieldBlocked || 0)
        )
      : result?.dealt ?? getBossAttack(game).damage);
  return {
    dieId: game.selectedDie,
    faceValues: getAttackDieFaceValues(
      game.selectedDie,
      damage,
      game.powerUp?.faceValues || getCoreDieFaceValues(game)
    ),
  };
};

export const getBossCounterAttack = (game, attack = getBossAttack(game)) => {
  const lockedCounter = getBossRoundState(game)?.lockedCounter;
  if (lockedCounter) return lockedCounter;
  const intent = getBossIntent(game);
  const boardRoll = getBossBoardAdjustedRoll(game);
  const startingHealth = getBossStartingHealth(game);
  const remaining = Math.max(0, startingHealth.bossHp - attack.damage);
  const won = remaining === 0;
  const landing = getBoardSquare(game.bossDoorBoard, game.bossLandingIndex);
  const attackType = landing?.effect?.type;
  // Preserve these snapshot keys for older completed rounds. Current boards
  // never apply self-damage: only the player's attack reduces the Core's HP.
  const skullDamage = 0;
  const bossHpAfterSkull = remaining;
  if (
    !Number.isInteger(game.bossRoll) ||
    !Number.isInteger(boardRoll) ||
    won ||
    bossHpAfterSkull === 0
  ) {
    return {
      naturalRoll: game.bossRoll,
      boardRoll,
      target: null,
      hit: false,
      won: won || bossHpAfterSkull === 0,
      skullDamage,
      bossHpAfterPlayerImpact: remaining,
      bossHpAfterSkull,
      baseDamage: 0,
      attackType,
      massiveAttackDamage: null,
      stanceBonus: 0,
      overclockBonus: 0,
      rawDamage: 0,
      reductions: [],
      decoyIntercepted: false,
      decoyDamage: 0,
      protocolSaved: false,
      protocolPrevented: 0,
      damage: 0,
    };
  }

  const hit = Math.max(0, boardRoll - intent.interference) > 0;
  const { healed } = getBossPlayerHealing(game);
  const hpBeforeImpact = startingHealth.hp + healed;
  const massiveAttackDamage = null;
  const baseDamage = Math.max(0, boardRoll - intent.interference);
  const stanceBonus = hit && game.stance === 'overload' ? 3 : 0;
  const overclockBonus = hit && game.relics.includes('overclock') ? 1 : 0;
  const rawDamage = baseDamage + stanceBonus + overclockBonus;
  let damage = rawDamage;
  const reductions = [];
  const boardShieldAmount = getBoardEffectTotal(game, 'shield');
  if (damage > 0 && boardShieldAmount > 0) {
    const blocked = Math.min(boardShieldAmount, damage);
    damage -= blocked;
    reductions.push({
      id: 'boardShield',
      amount: blocked,
      message: `Board shield blocked ${blocked} incoming damage`,
    });
  }
  if (hit && game.stance === 'guard' && damage > 0) {
    damage = Math.max(0, damage - 1);
    reductions.push({
      id: 'guard',
      amount: 1,
      message: 'Guard blocked 1 incoming damage',
    });
  }
  const randomBlock = game.bossUpgradeRolls?.rouletteAegis?.block || 0;
  if (damage > 0 && randomBlock > 0) {
    const blocked = Math.min(randomBlock, damage);
    damage -= blocked;
    reductions.push({
      id: 'rouletteAegis',
      amount: blocked,
      message: `Random aegis blocked ${blocked} incoming damage`,
    });
  }
  getRelicBlockEffects(game, {
    adjustedRoll: getBoardAdjustedRoll(game),
  }).forEach(({ id, amount }) => {
    if (damage > 0) {
      const blocked = Math.min(amount, damage);
      damage -= blocked;
      reductions.push({
        id,
        amount: blocked,
        message: `${RELICS[id].name} blocked ${blocked} incoming damage`,
      });
    }
  });
  // Save the interception with the round at impact so spending the beacon
  // cannot change the damage calculation or the outcome after a reload.
  const round = getBossRoundState(game);
  const decoyIntercepted = round?.counterImpactApplied
    ? round.decoyIntercepted === true
    : damage > 0 && getDecoyBeaconState(game).deployed;
  const decoyDamage = decoyIntercepted ? damage : 0;
  const incomingDamage = decoyIntercepted ? 0 : damage;
  const protocolSaved =
    incomingDamage >= hpBeforeImpact && didLastStandSurvive(game);
  const protocolDamage = Math.max(0, hpBeforeImpact - 1);
  const protocolPrevented = protocolSaved ? incomingDamage - protocolDamage : 0;
  return {
    naturalRoll: game.bossRoll,
    boardRoll,
    target: null,
    hit,
    won: won || bossHpAfterSkull === 0,
    skullDamage,
    bossHpAfterPlayerImpact: remaining,
    bossHpAfterSkull,
    baseDamage,
    attackType,
    massiveAttackDamage,
    stanceBonus,
    overclockBonus,
    rawDamage,
    reductions,
    decoyIntercepted,
    decoyDamage,
    protocolSaved,
    protocolPrevented,
    damage: protocolSaved ? protocolDamage : incomingDamage,
  };
};

export const getBossIncomingDamage = (game) =>
  getBossCounterAttack(game).damage;

export const getBossRoundHealth = (game) => {
  const lockedHealth = getBossRoundState(game)?.lockedHealth;
  if (lockedHealth) return lockedHealth;
  const starting = getBossStartingHealth(game);
  const { healed } = getBossPlayerHealing(game);
  const afterHealing = applyHealing(starting, healed);
  const counter = getBossCounterAttack(game);
  const bossHealed =
    counter.damage > 0 && !counter.won
      ? Math.min(
          getBossIntent(game).heal,
          BOSS_MAX_HP - counter.bossHpAfterSkull
        )
      : 0;
  return {
    hpBefore: starting.hp,
    maxHpBefore: starting.maxHp,
    bossHpBefore: starting.bossHp,
    hpAfterHealing: afterHealing.hp,
    maxHpAfterHealing: afterHealing.maxHp,
    maxHpGained: afterHealing.maxHp - starting.maxHp,
    hpAfterImpact: Math.max(0, afterHealing.hp - counter.damage),
    bossHpAfterPlayerImpact: counter.bossHpAfterPlayerImpact,
    bossHpAfterSkull: counter.bossHpAfterSkull,
    bossHealed,
    bossHpAfterHealing: counter.bossHpAfterSkull + bossHealed,
  };
};

export const requestBossLastStand = (game) => {
  if (
    game.phase !== 'bossRolled' ||
    !Number.isInteger(game.bossLandingIndex) ||
    getBossRoundState(game)?.counterImpactApplied ||
    !canTriggerLastStand(game)
  )
    return game;
  const health = getBossRoundHealth(game);
  const counter = getBossCounterAttack(game);
  return health.hpAfterImpact === 0 && !counter.won
    ? requestLastStandRoll(game, 'boss', counter.damage, health.hpAfterHealing)
    : game;
};

export const lockBossPlayerAttack = (game) => {
  game = flipRoll(game);
  if (
    !['bossPlayerRolled', 'bossRolled'].includes(game.phase) ||
    needsBoardTileChoice(game) ||
    getBossRoundState(game)
  )
    return game;
  return {
    ...game,
    bossRoundState: {
      round: game.bossRound,
      hpBefore: game.hp,
      maxHpBefore: getMaxHp(game),
      bossHpBefore: game.bossHp,
      corePhaseBefore: getCorePhase(game),
      attack: getBossAttack(game),
      healing: getBossPlayerHealing(game),
      playerImpactApplied: false,
      playerHealingApplied: false,
      coreSelfImpactApplied: false,
      counterImpactApplied: false,
      coreHealingApplied: false,
    },
  };
};

// Each visual impact commits its health once. All HUDs read these saved values;
// round resolution uses the original health snapshot instead of damaging it again.
const commitBossHealthEvent = (game, event, getChanges) => {
  if (!['bossPlayerRolled', 'bossRolled'].includes(game.phase)) return game;
  const locked = lockBossPlayerAttack(game);
  const round = getBossRoundState(locked);
  if (!round || round[event]) return game;
  const changes = getChanges(getBossRoundHealth(locked));
  const nextBossHp = changes.bossHp ?? locked.bossHp;
  const next = {
    ...locked,
    ...changes,
    corePhase: getAdvancedCorePhase(
      locked.corePhase,
      locked.bossHp,
      nextBossHp
    ),
    bossRoundState: { ...round, [event]: true },
  };
  // The current player hit and its healing are already locked. The spell can
  // change future player landings and the Core's not-yet-thrown board safely.
  return event === 'playerImpactApplied'
    ? applyCoreBoardPhase(next, next.corePhase)
    : next;
};

export const commitBossPlayerImpact = (game) =>
  commitBossHealthEvent(game, 'playerImpactApplied', (health) => ({
    bossHp: health.bossHpAfterPlayerImpact,
    coreShield: 0,
  }));

export const commitBossPlayerHealing = (game) => {
  const healed = commitBossHealthEvent(
    game,
    'playerHealingApplied',
    (health) => ({
      hp: health.hpAfterHealing,
      maxHp: health.maxHpAfterHealing,
    })
  );
  return healed === game
    ? game
    : consumeRelicHealingEffects(
        healed,
        getBossPlayerHealing(healed).relicEffects || []
      );
};

export const commitBossCounterImpact = (game) => {
  if (game.lastStandProtocol?.pending) return game;
  const requested = requestBossLastStand(game);
  if (requested !== game) return requested;
  const healed = commitBossPlayerHealing(game);
  if (getBossRoundState(healed)?.counterImpactApplied) return healed;
  const counter = getBossCounterAttack(healed);
  const impacted = commitBossHealthEvent(
    healed,
    'counterImpactApplied',
    (health) => ({
      hp: health.hpAfterImpact,
    })
  );
  if (impacted === healed) return healed;
  const resolved = counter.decoyIntercepted
    ? consumeDecoyBeacon(impacted)
    : impacted;
  return {
    ...resolved,
    bossRoundState: {
      ...resolved.bossRoundState,
      decoyIntercepted: counter.decoyIntercepted === true,
      protocolSaved: counter.protocolSaved === true,
    },
  };
};

export const commitBossCoreHealing = (game) =>
  commitBossHealthEvent(game, 'coreHealingApplied', (health) => ({
    bossHp: health.bossHpAfterHealing,
  }));

export const resolveBossRound = (game) => {
  game = flipRoll(game);
  if (
    !['bossPlayerRolled', 'bossRolled'].includes(game.phase) ||
    needsBoardTileChoice(game) ||
    game.lastStandProtocol?.pending
  )
    return game;
  const unresolvedGame = game;
  game = lockBossPlayerAttack(game);
  const attack = getBossAttack(game);
  const roundHealth = getBossRoundHealth(game);
  const lethalPlayerAttack = roundHealth.bossHpAfterPlayerImpact === 0;
  if (
    !lethalPlayerAttack &&
    (game.phase !== 'bossRolled' || !Number.isInteger(game.bossRoll))
  )
    return unresolvedGame;
  const counter = getBossCounterAttack(game, attack);
  const requested = requestBossLastStand(game);
  if (requested !== game) return requested;
  const intent = getBossIntent(game);
  const won = counter.bossHpAfterSkull === 0;
  const bossRoll = counter.boardRoll ?? game.bossRoll;
  const hit = counter.hit;
  const relicActivations = [...attack.effects];
  const {
    patchHeal,
    swarmHeal,
    boardHeal,
    healed,
    relicEffects = [],
  } = getBossPlayerHealing(game);
  if (relicEffects.length)
    relicActivations.push(
      ...relicEffects.map(({ id, message }) => ({ id, message }))
    );
  else if (patchHeal)
    relicActivations.push({
      id: 'patchKit',
      message: `Repair pulse: + ${patchHeal} Max HP`,
    });
  if (swarmHeal > 0)
    relicActivations.push({
      id: 'naniteSwarm',
      message: `Random repair: + ${swarmHeal} Max HP`,
    });
  if (boardHeal > 0)
    relicActivations.push({
      id: 'boardHeal',
      message: `Recovery tile: + ${boardHeal} Max HP`,
    });
  const damage = counter.damage;
  const survivalRoll =
    game.lastStandProtocol?.lastAttempt?.encounter ===
    getLastStandEncounter(game)
      ? game.lastStandProtocol.lastAttempt
      : null;
  if (survivalRoll) {
    relicActivations.push({
      id: 'lastStandProtocol',
      message: survivalRoll.success
        ? `Survival roll ${formatDieValue(
            survivalRoll.finalRoll
          )} beat 75. You survived at 1 HP. Protocol consumed.`
        : `Survival roll ${formatDieValue(
            survivalRoll.finalRoll
          )} did not beat 75. Protocol consumed.`,
    });
  }
  if (counter.decoyIntercepted) {
    relicActivations.push({
      id: 'decoyBeacon',
      message: `Hologram intercepted the attack. Prevented ${counter.decoyDamage} HP damage.`,
    });
  }
  if (counter.overclockBonus > 0) {
    relicActivations.push({
      id: 'overclockBacklash',
      name: 'Overclock Backlash',
      icon: '!',
      message: 'Unsafe power added 1 incoming damage',
    });
  }
  counter.reductions
    .filter(({ id }) => id !== 'guard')
    .forEach(({ id, message }) => relicActivations.push({ id, message }));
  const victoryHealingEffects = getRelicHealingEffects(game, {
    victory: won,
    includeAttack: false,
  });
  const victoryHeal = victoryHealingEffects.reduce(
    (sum, { amount }) => sum + amount,
    0
  );
  const finalHealth = applyHealing(
    {
      hp: roundHealth.hpAfterImpact,
      maxHp: roundHealth.maxHpAfterHealing,
    },
    victoryHeal
  );
  const hp = finalHealth.hp;
  const shardEffects = getRelicShardEffects(game, {
    victory: won,
    adjustedRoll: getBoardAdjustedRoll(game),
    attack: attack.damage,
    enemyHpBefore: roundHealth.bossHpBefore,
  });
  const earned = shardEffects.reduce((sum, { amount }) => sum + amount, 0);
  const shards = game.shards + earned;
  relicActivations.push(
    ...victoryHealingEffects.map(({ id, message }) => ({ id, message })),
    ...shardEffects.map(({ id, message }) => ({ id, message }))
  );
  const bossHealed = roundHealth.bossHealed;
  const bossHp = roundHealth.bossHpAfterHealing;
  const lost = hp === 0;
  const bossBoardLanding = getBoardSquare(
    game.bossDoorBoard,
    game.bossLandingIndex
  );
  const attackDealt = Math.min(roundHealth.bossHpBefore, attack.damage);
  const runBestAttack = Math.max(game.runBestAttack || 0, attack.damage);
  const bestAttack = Math.max(game.bestAttack || 0, runBestAttack);
  const victoryScore = won
    ? getVictoryScore({
        hp,
        shards,
        relics: game.relics,
        bossRound: game.bossRound,
      })
    : null;
  const finalScore =
    victoryScore ||
    (lost
      ? getDefeatScore({
          floor: CORE_SECTOR,
          shards,
          relics: game.relics,
          enemyMaxHp: BOSS_MAX_HP,
          enemyHp: bossHp,
        })
      : null);
  const score = finalScore?.score || 0;
  const scoreBreakdown = finalScore?.scoreBreakdown || [];
  const result = {
    boss: true,
    round: game.bossRound,
    roomName: 'The Glitch Core',
    success: won || !hit || counter.decoyIntercepted,
    effectiveRoll: getBoardAdjustedRoll(game),
    target: null,
    attack: intent.name,
    dealt: attackDealt,
    attackDealt,
    naturalRoll: game.roll,
    enemy: { name: 'The Glitch Core' },
    enemyHpBefore: roundHealth.bossHpBefore,
    enemyHpAfter: bossHp,
    playerHpBefore: roundHealth.hpBefore,
    playerHpAfter: hp,
    retaliation: !lethalPlayerAttack,
    counterDamage:
      counter.baseDamage + counter.stanceBonus + counter.overclockBonus,
    damageCalculation: game.bossRoundState?.damageCalculation
      ? {
          ...game.bossRoundState.damageCalculation,
          counterRows: game.bossRoundState.counterCalculationRows || [],
          defenses: counter.reductions.map(({ id, amount }) => ({
            name: `${id} blocked`,
            amount,
          })),
        }
      : null,
    attackDamageBeforeShield: attack.damageBeforeShield,
    boardShieldBlocked: attack.boardShieldBlocked,
    bossSelfDamage: counter.skullDamage,
    bossAttackType: counter.attackType,
    massiveAttackDamage: counter.massiveAttackDamage,
    damage,
    healed: healed + victoryHeal,
    healedOnLanding: boardHeal,
    maxHpOnLanding: Math.max(
      0,
      roundHealth.hpBefore + boardHeal - roundHealth.maxHpBefore
    ),
    maxHpGained: finalHealth.maxHp - roundHealth.maxHpBefore,
    boardTreasureReward: game.boardTreasureReward || null,
    boardTreasureRewards: getBoardTreasureRewards(game),
    chainReactionActivation: game.chainReactionActivation,
    throwCombo: game.throwCombo,
    bossHealed,
    earned,
    bossRoll: game.bossRoll,
    bossEffectiveRoll: bossRoll,
    bossBoardLanding:
      bossBoardLanding && Number.isInteger(game.bossLandingIndex)
        ? { ...bossBoardLanding, index: game.bossLandingIndex }
        : null,
    hit,
    decoyIntercepted: counter.decoyIntercepted === true,
    decoyStillDeployed: !!(
      game.decoyBeacon?.deployed &&
      !game.decoyBeacon.used &&
      !counter.decoyIntercepted
    ),
    protocolSaved: counter.protocolSaved,
    survivalRoll,
    relicActivations,
    scoreBreakdown,
    ...(won && {
      victoryDie: {
        dieId: game.selectedDie,
        faceValues: getAttackDieFaceValues(
          game.selectedDie,
          lethalPlayerAttack ? attack.damage : counter.skullDamage,
          game.powerUp?.faceValues || getCoreDieFaceValues(game)
        ),
      },
    }),
  };
  return {
    ...consumeRelicHealingEffects(
      counter.decoyIntercepted ? consumeDecoyBeacon(game) : game,
      relicEffects
    ),
    hp,
    maxHp: finalHealth.maxHp,
    shards,
    totalShardsAcquired: acquireShards(game, earned),
    bossHp,
    bossBattleStats: recordBossBattleRound(
      game.bossBattleStats,
      attack,
      counter
    ),
    corePhase: getAdvancedCorePhase(game.corePhase, game.bossHp, bossHp),
    coreShield:
      bossBoardLanding?.effect?.type === 'shield'
        ? bossBoardLanding.effect.amount
        : 0,
    bossRoundState: {
      ...game.bossRoundState,
      playerImpactApplied: true,
      playerHealingApplied: true,
      coreSelfImpactApplied: true,
      counterImpactApplied: true,
      decoyIntercepted: counter.decoyIntercepted === true,
      coreHealingApplied: true,
    },
    phase: won ? 'won' : lost ? 'lost' : 'bossResult',
    score,
    bestScore: won || lost ? Math.max(game.bestScore, score) : game.bestScore,
    newBestScore: (won || lost) && score > game.bestScore,
    runBestAttack,
    bestAttack,
    victories: game.victories + (won ? 1 : 0),
    lastResult: result,
    history: getRecentRooms([result, ...game.history]),
  };
};

export const nextBossRound = (game) =>
  game.phase !== 'bossResult'
    ? game
    : applyCoreBoardPhase(
        {
          ...game,
          phase: 'bossReady',
          bossCombatVersion: 2,
          lastCoreAttack: game.lastResult,
          boardCondition: null,
          boardTileResolution: null,
          throwCombo: null,
          bossRound: game.bossRound + 1,
          powerUp: null,
          ghostBump: { ...game.ghostBump, armed: false, throwRound: null },
          decoyBeacon: { ...game.decoyBeacon, throwRound: null },
          phaseAnchor: { ...game.phaseAnchor, armed: false },
          portalPair: createEmptyPortalPair(),
          bossRoundState: null,
          doorBoard:
            game.doorBoardVersion === DOOR_BOARD_VERSION
              ? game.doorBoard
              : migratePlayerBoardBalance({
                  ...game,
                  phase: 'bossReady',
                  doorBoard: withPlayerBoardCorners(game.doorBoard),
                }).doorBoard,
          doorBoardVersion: DOOR_BOARD_VERSION,
          bossDoorBoard: strengthenCoreBoard(
            generateBossDoorBoard(),
            getCorePhase(game)
          ),
          bossDoorBoardVersion: BOSS_BOARD_VERSION,
          roll: null,
          bossRoll: null,
          bossMassiveAttackDamage: null,
          landingIndex: null,
          bossLandingIndex: null,
          boardModifier: 0,
          bossBoardModifier: 0,
          bossUpgradeRolls: {},
          boardTreasureReward: null,
          boardTreasureRewards: [],
          chainReactionActivation: null,
          flipped: false,
          coreStyleVersion: 1,
          coreDieFaces: createCoreDieFaces(game.selectedDie),
          coreDieCurrentFaces: null,
          lastResult: null,
        },
        getCorePhase(game)
      );
